import React, {Component} from 'react';
import {
  Dropdown,
  Table,
  Icon,
  Button,
  Responsive,
  Pagination,
} from 'semantic-ui-react';
import produce from 'immer';
import {PortalWithState} from 'react-portal';
import {Spring} from 'react-spring/renderprops';
import {format} from 'date-fns';
import styled from 'react-emotion';

import epicorApi from '../../store/services/epicor';
import {ModalWrapper} from '../modal/ModalWrapper';
import {SerialNumberDetail} from '../dashboard/SerialNumberDetail';

const StyledRoot = styled.div`
  .result-property {
    display: flex;
    align-items: center;
    min-height: 3.3rem;
  }

  .result-property-label {
    width: 10rem;
    align-items: center;
    display: flex;

    label {
      font-weight: bold;
      color: #9a8e7f;
    }

    @media (max-width: 481px) {
      width: 5rem;
      min-width: 5rem;
      font-size: 0.8rem;
    }
  }

  .result-property-value {
    border-left: solid 2px #babbb3;
    min-height: 3.3rem;
    display: flex;
    align-items: center;
    padding-left: 10px;
  }
`;

class OrderTable extends Component {
  state = {
    selectedResult: undefined,
    ordersWithParts: {},
    partsWithSerials: {},
    pageInfo: undefined,
    loading: false,
    activePage: 1,
  };

  _mutate = (draftFn, cb) => {
    this.setState(produce(draftFn), cb);
  };

  _showDetail = selectedResult => {
    this._openPortal();
    this.setState({selectedResult});
  };

  _renderDetailModal = () => {
    const {selectedResult} = this.state;

    return (
      <Spring from={{right: '-100%'}} to={{right: '0%'}}>
        {styles => (
          <ModalWrapper style={styles}>
            <SerialNumberDetail
              key={selectedResult}
              sysRowId={selectedResult}
              close={this._closePortal}
            />
          </ModalWrapper>
        )}
      </Spring>
    );
  };

  _fetchPartsByOrder = async order => {
    if (!this.state.ordersWithParts[order.sysRowID]) {
      this._mutate(draft => {
        draft.ordersWithParts[order.sysRowID] = {
          loading: true,
        };
      });

      const result = await epicorApi.fetchPartsByOrderSysRowID(order.sysRowID);
      this._mutate(draft => {
        draft.ordersWithParts[order.sysRowID].loading = false;
        draft.ordersWithParts[order.sysRowID].result = {
          ...result,
          partOptions: result.parts.map(part => {
            const revNum = part.revistionNumber
              ? ` ${part.revistionNumber}`
              : '';
            return {
              key: part.partNum,
              value: part.partNum,
              text: `${part.partNum}${revNum}`,
            };
          }),
        };
      });
    }
  };

  _selectPartNumber = ({orderWithParts, sysRowID, value}) => {
    const {result: selected = {}} = orderWithParts;
    const partKey = `${selected.orderNum}|${value}`;
    this._mutate(
      draft => {
        draft.ordersWithParts[sysRowID].selected = value;
      },
      async () => {
        this._mutate(draft => {
          draft.partsWithSerials[partKey] = {
            loading: true,
          };
        });
        const {result} = await epicorApi.fetchOrderPartSerials(
          selected.orderNum,
          value
        );

        this._mutate(draft => {
          draft.partsWithSerials[partKey].loading = false;
          draft.partsWithSerials[partKey].result = result;
        });
      }
    );
  };

  _renderRow = (result, isLargeScreen) => {
    const orderWithParts = this.state.ordersWithParts[result.sysRowID] || {};
    const {result: selected = {}} = orderWithParts;
    const partKey = `${selected.orderNum}|${orderWithParts.selected}`;
    const partWithSerials = this.state.partsWithSerials[partKey] || {};

    const partOptions = orderWithParts.result
      ? orderWithParts.result.partOptions
      : [];

    const partSerialOptions = (partWithSerials.result || []).map(x => ({
      value: x.sysRowID,
      text: x.serialNumber,
    }));

    return (
      <Table.Row key={result.sysRowID}>
        {isLargeScreen ? (
          <React.Fragment>
            <Table.Cell>{result.poNum}</Table.Cell>
            <Table.Cell>
              {format(new Date(result.orderDate), 'YYYY-MM-DD')}
            </Table.Cell>
            <Table.Cell style={{width: 250}}>
              <Dropdown
                loading={orderWithParts.loading}
                search
                selectOnNavigation={false}
                selection
                fluid
                placeholder="PART NUMBER"
                options={partOptions}
                noResultsMessage={
                  orderWithParts.loading
                    ? 'Loading...'
                    : 'No serial numbers found'
                }
                onOpen={() => {
                  this._fetchPartsByOrder(result);
                }}
                onChange={(_, {value}) =>
                  this._selectPartNumber({
                    orderWithParts,
                    sysRowID: result.sysRowID,
                    value,
                  })
                }
                value={orderWithParts.selected}
              />
            </Table.Cell>
            <Table.Cell>
              <Dropdown
                disabled={!orderWithParts.selected}
                loading={partWithSerials.loading}
                placeholder="SERIAL NUMBER"
                search
                selection
                noResultsMessage="No serial numbers found"
                options={partSerialOptions}
                onChange={(_, {value}) => {
                  this._mutate(draft => {
                    draft.partsWithSerials[partKey].selected = value;
                  });
                }}
                value={partWithSerials.selected}
              />
              <Button
                type="button"
                disabled={!partWithSerials.selected}
                className="clear action"
                icon
                onClick={() => {
                  this._showDetail(partWithSerials.selected);
                }}
                style={{
                  position: 'relative',
                }}
              >
                GO
              </Button>
            </Table.Cell>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Table.Cell style={{padding: '0 0 0 10px'}}>
              <div className="result-property">
                <span className="result-property-label">
                  <label>P.O. Number</label>
                </span>
                <span className="result-property-value">
                  <span>{result.poNum}</span>
                </span>
              </div>
              <div className="result-property">
                <span className="result-property-label">
                  <label>Date</label>
                </span>
                <span className="result-property-value">
                  <span>
                    {format(new Date(result.orderDate), 'YYYY-MM-DD')}
                  </span>
                </span>
              </div>
              <div className="result-property">
                <span className="result-property-label">
                  <label>Part Number</label>
                </span>
                <span className="result-property-value">
                  <span>
                    <Dropdown
                      loading={orderWithParts.loading}
                      search
                      selectOnNavigation={false}
                      selection
                      placeholder="PART NUMBER"
                      options={partOptions}
                      noResultsMessage={
                        orderWithParts.loading
                          ? 'Loading...'
                          : 'No serial numbers found'
                      }
                      onOpen={() => {
                        this._fetchPartsByOrder(result);
                      }}
                      onChange={(_, {value}) =>
                        this._selectPartNumber({
                          orderWithParts,
                          sysRowID: result.sysRowID,
                          value,
                        })
                      }
                      value={orderWithParts.selected}
                    />
                  </span>
                </span>
              </div>
              <div className="result-property">
                <span className="result-property-label">
                  <label>Serial Number</label>
                </span>
                <span className="result-property-value">
                  <span>
                    <Dropdown
                      disabled={!orderWithParts.selected}
                      loading={partWithSerials.loading}
                      placeholder="SERIAL NUMBER"
                      search
                      selection
                      noResultsMessage="No serial numbers found"
                      options={partSerialOptions}
                      onChange={(_, {value}) => {
                        this._mutate(draft => {
                          draft.partsWithSerials[partKey].selected = value;
                        });
                      }}
                      value={partWithSerials.selected}
                    />
                    <Button
                      type="button"
                      disabled={!partWithSerials.selected}
                      className="clear action"
                      icon
                      onClick={() => {
                        this._showDetail(partWithSerials.selected);
                      }}
                    >
                      <Icon name="chevron right" />
                    </Button>
                  </span>
                </span>
              </div>
            </Table.Cell>
          </React.Fragment>
        )}
      </Table.Row>
    );
  };

  render() {
    const {orders} = this.props;
    const {activePage} = this.state;
    const pageSize = 20;

    return (
      <PortalWithState closeOnEsc>
        {({openPortal, closePortal, portal}) => {
          this._closePortal = closePortal;
          this._openPortal = openPortal;
          return (
            <StyledRoot>
              <Responsive minWidth={940}>
                <Table basic="very" striped>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell style={{width: 200}}>
                        P.O. Number
                      </Table.HeaderCell>
                      <Table.HeaderCell>Date</Table.HeaderCell>
                      <Table.HeaderCell>Part Number</Table.HeaderCell>
                      <Table.HeaderCell>Serial Number</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>

                  <Table.Body>
                    {orders
                      .slice((activePage - 1) * pageSize, activePage * pageSize)
                      .map(order => this._renderRow(order, true))}
                  </Table.Body>
                </Table>
              </Responsive>
              <Responsive maxWidth={939}>
                <Table basic="very" striped>
                  <Table.Body>
                    {orders
                      .slice((activePage - 1) * pageSize, activePage * pageSize)
                      .map(order => this._renderRow(order))}
                  </Table.Body>
                </Table>
              </Responsive>
              {portal(this._renderDetailModal())}
              <Pagination
                defaultActivePage={1}
                onPageChange={async (e, paginationInfo) => {
                  this.setState({activePage: paginationInfo.activePage});
                }}
                ellipsisItem={{
                  content: <Icon name="ellipsis horizontal" />,
                  icon: true,
                }}
                firstItem={{
                  content: <Icon name="angle double left" />,
                  icon: true,
                }}
                lastItem={{
                  content: <Icon name="angle double right" />,
                  icon: true,
                }}
                prevItem={{content: <Icon name="angle left" />, icon: true}}
                nextItem={{
                  content: <Icon name="angle right" />,
                  icon: true,
                }}
                totalPages={Math.ceil(orders.length / pageSize)}
              />
            </StyledRoot>
          );
        }}
      </PortalWithState>
    );
  }
}

export default OrderTable;
